import { GalleryApiErrorCode, GalleryItemErrorCode } from '@/types/galleries'
import type { GalleryApiError } from '@/utils/galleries/apiError'
import { UploadRejectReason } from '@/utils/galleries/uploadPlan'

export const UploadFailureReason = {
  ...UploadRejectReason,
  NotEnoughStorage: 'not_enough_storage',
  ItemLimit: 'item_limit',
  FileTooLarge: 'file_too_large',
  PrepareFailed: 'prepare_failed',
  UploadFailed: 'upload_failed',
  ContentMismatch: 'content_mismatch',
  GalleryDeleted: 'gallery_deleted',
  ProcessingFailed: 'processing_failed',
  ImageTooManyPixels: 'image_too_many_pixels',
  UnsupportedContainer: 'unsupported_container',
  LocationMetadata: 'location_metadata',
  MetadataUnverifiable: 'metadata_unverifiable',
} as const
export type UploadFailureReason = typeof UploadFailureReason[keyof typeof UploadFailureReason]

export interface UploadFailure {
  reason: UploadFailureReason
  retryable: boolean
}

/** Maps the item `error_code` of a failed gallery item to a failure with retry rules. */
export function failureForItemErrorCode(code: GalleryItemErrorCode | null): UploadFailure {
  switch (code) {
    case GalleryItemErrorCode.UnsupportedContainer:
      return { reason: UploadFailureReason.UnsupportedContainer, retryable: false }
    case GalleryItemErrorCode.LocationMetadataPresent:
      return { reason: UploadFailureReason.LocationMetadata, retryable: false }
    case GalleryItemErrorCode.ImageTooManyPixels:
      return { reason: UploadFailureReason.ImageTooManyPixels, retryable: true }
    case GalleryItemErrorCode.MetadataUnverifiable:
      return { reason: UploadFailureReason.MetadataUnverifiable, retryable: true }
    default:
      return { reason: UploadFailureReason.ProcessingFailed, retryable: true }
  }
}

export type ApiFailureOutcome =
  | { kind: 'failure'; failure: UploadFailure }
  | { kind: 'requeue' }

/** Maps a gallery API error from reserve or complete to a tile failure (or a re-queue). */
export function outcomeForApiError(error: GalleryApiError): ApiFailureOutcome {
  if (error.status === 404) {
    return { kind: 'failure', failure: { reason: UploadFailureReason.GalleryDeleted, retryable: false } }
  }
  switch (error.code) {
    case GalleryApiErrorCode.StorageQuotaExceeded:
      return { kind: 'failure', failure: { reason: UploadFailureReason.NotEnoughStorage, retryable: true } }
    case GalleryApiErrorCode.GalleryItemLimitReached:
      return { kind: 'failure', failure: { reason: UploadFailureReason.ItemLimit, retryable: false } }
    case GalleryApiErrorCode.UploadNotOpen:
      return error.uploadStatus === 'aborted'
        ? { kind: 'failure', failure: { reason: UploadFailureReason.GalleryDeleted, retryable: false } }
        : { kind: 'failure', failure: { reason: UploadFailureReason.UploadFailed, retryable: true } }
    case GalleryApiErrorCode.TooManyOpenUploads:
      return { kind: 'requeue' }
    case GalleryApiErrorCode.FileTooLarge:
    case GalleryApiErrorCode.TooLarge:
      return { kind: 'failure', failure: { reason: UploadFailureReason.FileTooLarge, retryable: false } }
    case GalleryApiErrorCode.ContentInvalid:
    case GalleryApiErrorCode.SizeMismatch:
      return { kind: 'failure', failure: { reason: UploadFailureReason.ContentMismatch, retryable: true } }
    default:
      return { kind: 'failure', failure: { reason: UploadFailureReason.UploadFailed, retryable: true } }
  }
}
